import { createSlice } from '@reduxjs/toolkit';
import { message as toaster } from 'antd';
import { API } from 'api';

const initialState = {};

export const routeSliceV2 = createSlice({
	name: 'routeStateV2',
	initialState,
	reducers: {},
});

/**
 * Search Routes V2
 * Endpoint: POST /api/v2/search
 * @param {Object} params.filter_by - route_name, project_id, warehouse, movement_type, is_active, created_from, created_to
 * @param {Object} params.pagination - { page, per_page }
 * @param {Object} params.sort_by - { field, order }
 */
export const getRouteListV2 =
	({ filter_by = {}, pagination = {}, sort_by = null }) =>
	async () => {
		const cleanFilterBy = {};
		if (filter_by) {
			Object.keys(filter_by).forEach((key) => {
				if (
					filter_by[key] !== undefined &&
					filter_by[key] !== null &&
					filter_by[key] !== ''
				) {
					cleanFilterBy[key] = filter_by[key];
				}
			});
		}

		const page = pagination.page || 1;
		const limit = pagination.per_page || 20;
		const offset = (page - 1) * limit;

		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'route',
				offset,
				limit,
				fetch_filters: true,
				filter_list: {},
				filter_by: cleanFilterBy,
				...(sort_by && {
					sort_by: {
						field: sort_by.field || 'created_at',
						order: sort_by.order || 'desc',
					},
				}),
			},
			version: 'v2',
		};

		try {
			const response = await API.common(CREDENTIALS);
			const rows = response?.data?.routes || response?.data?.rows || [];
			const paginationData = response?.data?.pagination || {};
			const count =
				paginationData?.total ||
				response?.meta?.count ||
				response?.data?.count ||
				rows.length;
			return { rows, count, pagination: { page, per_page: limit } };
		} catch (error) {
			toaster.error(error?.message || 'Failed to fetch routes');
			return { rows: [], count: 0, pagination: { page: 1, per_page: limit } };
		}
	};

/**
 * Search Route Filters V2
 * Endpoint: POST /api/v2/list_search
 * Runs a single query and returns only { id, label, value },
 * @param {Object} params.filter_by - query_str, is_active, project_id, etc.
 * @param {number} params.limit - max results
 */
export const searchRouteFiltersV2 =
	({ filter_by = {}, limit = 1000 }) =>
	async () => {
		const cleanFilterBy = {};
		if (filter_by) {
			Object.keys(filter_by).forEach((key) => {
				if (
					filter_by[key] !== undefined &&
					filter_by[key] !== null &&
					filter_by[key] !== ''
				) {
					cleanFilterBy[key] = filter_by[key];
				}
			});
		}
		// Default to active routes only, unless the caller explicitly asked for
		// something else (e.g. is_active: false to list inactive ones).
		if (cleanFilterBy.is_active === undefined) {
			cleanFilterBy.is_active = true;
		}

		const validatedLimit = Math.min(Math.max(limit || 1000, 1), 1000);

		const CREDENTIALS = {
			url: '/list_search',
			data: {
				list_search_type: 'route',
				filter_by: cleanFilterBy,
				limit: validatedLimit,
				offset: 0,
			},
			version: 'v2',
		};

		try {
			const response = await API.common(CREDENTIALS);
			const results = response?.data?.results || [];
			return {
				results: results.map((item) => ({
					value: item.value ?? item.id,
					label: item.label,
				})),
				total: response?.data?.total || results.length,
			};
		} catch (error) {
			toaster.error(error?.message || 'Failed to fetch routes');
			return { results: [], total: 0 };
		}
	};

/**
 * Update Route Status V2
 */
export const updateRouteStatusV2 =
	({ id, is_active }) =>
	async () => {
		const CREDENTIALS = {
			url: '/update',
			data: {
				update_type: 'route',
				data: { id, is_active },
			},
			version: 'v2',
		};

		const res = await API.common(CREDENTIALS);
		toaster.success(
			res?.message || `Route ${is_active ? 'activated' : 'deactivated'} successfully`
		);
		return res;
	};

/**
 * Create Route V2
 */
export const createRouteV2 =
	({ req_data, setErrors }) =>
	async () => {
		const CREDENTIALS = {
			url: '/create',
			data: { create_type: 'route', data: req_data },
			setErrors,
			version: 'v2',
		};

		const res = await API.common(CREDENTIALS);
		toaster.success(res?.message || 'Route created successfully');
		return res;
	};

/**
 * Get Project Nodes V2
 * Endpoint: POST /api/v2/search
 * Payload: { search_type: 'project_nodes', filter_by: { project_id } }
 */
export const getProjectNodesV2 =
	({ project_id }) =>
	async () => {
		if (!project_id) return [];
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'project_nodes',
				filter_by: { project_id },
			},
			version: 'v2',
		};
		const response = await API.common(CREDENTIALS);
		return response?.data?.nodes ?? response?.data ?? [];
	};

/**
 * Get Route by ID V2
 */
export const getRouteByIdV2 =
	({ id }) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: { search_type: 'route', filter_by: { route_id: id } },
			version: 'v2',
		};

		const response = await API.common(CREDENTIALS);
		const routes = response?.data?.routes || response?.data?.rows || [];
		return routes.length > 0 ? routes[0] : null;
	};

/**
 * Update Route V2
 */
export const updateRouteV2 =
	({ id, req_data, setErrors }) =>
	async () => {
		const CREDENTIALS = {
			url: '/update',
			data: { update_type: 'route', data: { id, ...req_data } },
			setErrors,
			version: 'v2',
		};

		const res = await API.common(CREDENTIALS);
		toaster.success(res?.message || 'Route updated successfully');
		return res;
	};

/**
 * Export Routes CSV V2
 */
export const exportRouteCSVV2 =
	({ filter_by = {} }) =>
	async () => {
		const cleanFilterBy = {};
		if (filter_by) {
			Object.keys(filter_by).forEach((key) => {
				if (
					filter_by[key] !== undefined &&
					filter_by[key] !== null &&
					filter_by[key] !== ''
				) {
					cleanFilterBy[key] = filter_by[key];
				}
			});
		}

		const CREDENTIALS = {
			url: '/report',
			data: {
				report_type: 'route_export',
				format: 'csv',
				data: { filter_by: cleanFilterBy },
			},
			version: 'v2',
		};

		const response = await API.common(CREDENTIALS);
		const downloadUrl = response?.data?.download_url || response?.download_url;
		if (downloadUrl) {
			window.open(downloadUrl, '_blank');
		}
		return response;
	};

/**
 * Bulk Upload Routes V2
 * Uploads multiple routes from a CSV file
 * @param {Object} params - Upload parameters
 * @param {string} params.s3_key - S3 key of the uploaded CSV file
 * @param {Function} params.setErrors - Error handler function
 * @returns {Promise} API response with success/error counts and download URL
 */
export const bulkUploadRoutesV2 =
	({ s3_key, setErrors }) =>
	async () => {
		const data = {
			upload_type: 'route',
			data: {
				key: s3_key,
			},
		};

		const CREDENTIALS = {
			url: '/bulk-upload',
			data,
			setErrors,
			version: 'v2',
		};

		return API.common(CREDENTIALS)
			.then((res) => {
				if (res && res.status === 'success') {
					const { success_count, error_count, total_count, download_url } =
						res.data || {};

					// Show appropriate message based on results
					if (error_count > 0) {
						toaster.warning(
							`Bulk upload completed: ${success_count} succeeded, ${error_count} failed out of ${total_count} total`
						);
					} else {
						toaster.success(
							res.message || `Successfully uploaded ${success_count} routes`
						);
					}

					// Return full response including download_url
					return { ...res, download_url };
				}

				return res;
			})
			.catch((error) => {
				console.error('❌ [API] Route bulk upload error:', error);
				toaster.error(
					error.response?.data?.message ||
						error.message ||
						'Failed to process bulk upload'
				);

				// Return error in consistent format
				return {
					status: 'error',
					error: {
						code: error.response?.status || 'API_ERROR',
						message:
							error.response?.data?.message ||
							error.message ||
							'Failed to process bulk upload',
					},
				};
			});
	};

export default routeSliceV2.reducer;
